/**
 * UI strings for the lesson module: lesson player (les.*), capture (cap.*), feedback (fb.*),
 * celebrations (cel.*), draw together (dt.*), share the moment + public share (stm.*),
 * review prompt (rev.*). `vi` must cover every `en` key.
 */
export const en = {
  /* ------------------------------ lesson player ------------------------------ */
  'les.close': 'Leave lesson',
  'les.stepOf': 'Step {n} of {total}',
  'les.captionShow': 'Show the step words',
  'les.captionHide': 'Hide the step words',
  'les.partOf': 'Part {n} of {total}',
  'les.stepN': 'Step {n}',
  'les.captions': 'Captions',
  'les.music': 'Music',
  'les.voice': 'Teacher voice',
  'les.leaveTitle': 'Leave this lesson?',
  'les.leaveBody': 'No worries, we will keep your place so you can carry on later.',
  'les.leave': 'Leave',
  'les.stay': 'Keep drawing',
  'les.notFound': 'Hmm, this lesson wandered off.',
  'les.missing': 'This part is still being drawn. Let’s skip ahead!',
  'les.yourTurn': 'Your turn!',
  'les.yourTurnBody': 'Draw this step on your paper. Tap Ready when you are done.',
  'les.yourTurnTogether': 'Draw this step on your paper. Wait for {name} before tapping Ready.',
  'les.yourTurnSpoken': 'Your turn! Draw this step on your paper, then tap Ready.',
  'les.readyDone': 'Ready, all done',
  'les.catchUp': 'Catch up',
  'les.catchUpTogether': 'Watch again together',
  'les.tipsTitle': 'Guidance & tips',
  'les.tips': 'Show tips',
  'les.reference': 'Show reference picture',
  'les.hideReference': 'Hide reference picture',
  'les.playerControls': 'Lesson player controls',
  'les.back10': 'Back 10 seconds',
  'les.fwd10': 'Forward 10 seconds',
  'les.play': 'Play',
  'les.pause': 'Pause',
  'les.seek': 'Lesson timeline',
  'les.speed': 'Speed {n}×',
  'les.gotIt': 'Got it',
  'les.nextTip': 'Next tip',
  'les.notNow': 'Not now',
  'les.letsGo': 'Let’s go',
  'les.redirect.playground': 'Lots more drawings are waiting in the playground.',
  'les.redirect.journal': 'Your journal keeps every drawing you make.',
  'les.redirect.studio': 'Design your own picture in the studio.',
  'les.redirect.challenges': 'See this week’s challenge and fill your slots.',
  'les.redirect.path': 'Your next lesson is ready on the path.',
  'les.interactNice': 'Nice! Try it again or tap Got it.',
  'les.interactHint.duplicate': 'Tap the shape to copy it.',
  'les.interactHint.scatter': 'Tap to sprinkle little marks.',
  'les.interactHint.gravity': 'Tap the button and watch!',
  'les.interactHint.shadow': 'Move the sun and watch the shadow.',
  'les.interactHint.pressure': 'Slide to press lighter or harder.',
  'les.interactHint.symmetry': 'Tap on one side. The other side copies you!',
  'les.demo.duplicate': 'Copy the shape',
  'les.demo.scatter': 'Sprinkle marks',
  'les.demo.gravity': 'Turn on gravity',
  'les.demo.gravityUp': 'Float back up',
  'les.demo.shadow': 'Move the sun',
  'les.demo.pressure': 'Pencil pressure',
  'les.demo.light': 'light',
  'les.demo.medium': 'medium',
  'les.demo.hard': 'strong',
  'les.demo.symmetry': 'Add a mirrored dot',
  'les.interactHint.arrows': 'Tap an arrow to turn the strokes.',
  'les.interactHint.bodyDrag': 'Drag the ball and watch the body bend.',
  'les.demo.arrows': 'Turn the strokes',
  'les.demo.bodyDrag': 'Bend the body',
  'les.revealSlider': 'Reveal slider',
  'les.off': 'Off',
  'les.on': 'On',
  'les.toggleLabel': 'Show the last step',
  'les.chooseThis': 'Choose this one',
  'les.builderParts': 'Character parts',
  'les.part.face': 'Face',
  'les.part.ears': 'Ears',
  'les.part.eyes': 'Eyes',
  'les.part.brows': 'Brows',
  'les.part.mouth': 'Mouth',
  'les.part.eyeDistance': 'Eye gap',
  'les.part.eyeHeight': 'Eye height',
  'les.part.mouthHeight': 'Mouth height',
  'les.part.color': 'Colour',
  'les.slider.low': 'Low',
  'les.slider.mid': 'Middle',
  'les.slider.high': 'High',
  'les.startDrawing': 'Start drawing',
  'les.turnOf': '{name}’s turn',
  'les.turnBody': 'Pass the device over: it is time to snap the next drawing.',
  'les.snapTurn': 'Snap {name}’s drawing',
  'les.yourDrawing': 'Your drawing',
  'les.photoLooksGood': 'Looking good! Use this photo?',
  'les.usePhoto': 'Use this photo',
  'les.retake': 'Retake',
  'les.skipPhoto': 'Skip',
  'les.myDrawing': 'My drawing',
  'les.journalTitle': 'Add it to your journal',
  'les.titleLabel': 'Title',
  'les.descLabel': 'Description',
  'les.descPlaceholder': 'Write something about it (optional)',
  'les.feelingQuestion': 'How do you feel about your drawing?',
  'les.feelingLabel': 'How do you feel?',
  'les.feeling.1': 'Not happy',
  'les.feeling.2': 'A bit unsure',
  'les.feeling.3': 'Okay',
  'les.feeling.4': 'Happy',
  'les.feeling.5': 'Super proud',
  'les.filterLabel': 'Photo filter',
  'les.filter.none': 'Original',
  'les.filter.clean': 'Crisp',
  'les.filter.warm': 'Sunny',
  'les.filter.cool': 'Breezy',
  'les.filter.mono': 'Pencil',
  'les.filter.pop': 'Bright',
  'les.addToJournal': 'Add to my journal',
  'les.reason.stepByStep': 'It goes step by step',
  'les.reason.fun': 'It was fun',
  'les.reason.proud': 'I’m proud of my drawing',
  'les.reason.tooHard': 'It was a bit hard',
  'les.reason.tooEasy': 'It was too easy',
  'les.reason.wantHelp': 'I wanted more help',
  'les.send': 'Send',
  'les.pathDoneTitle': 'You finished the whole path!',
  'les.pathDoneBody': 'Amazing work on {path}. Where should we draw next?',
  'les.later': 'Maybe later',

  /* --------------------------------- capture --------------------------------- */
  'cap.frameTitle': 'Fit your drawing inside the frame',
  'cap.calibrationTitle': 'Draw a circle on paper, as wide as it is tall',
  'cap.journalTitle': 'Snap a drawing for your journal',
  'cap.canvasTitle': 'Draw it here with your finger',
  'cap.close': 'Close camera',
  'cap.reference': 'Reference drawing',
  'cap.refTitle': 'Draw this',
  'cap.refHide': 'Hide the picture',
  'cap.refShow': 'Show the picture',
  'cap.refEnlarge': 'Make the picture bigger',
  'cap.skip': 'Skip for now',
  'cap.shutter': 'Take photo',
  'cap.gallery': 'Choose from gallery',
  'cap.drawOnScreen': 'Draw on screen instead',
  'cap.starting': 'Opening the camera…',
  'cap.deniedTitle': 'We need the camera to see your drawing',
  'cap.deniedBody': 'Ask a grown-up to allow the camera, or choose a photo from the gallery.',
  'cap.noCameraTitle': 'No camera here',
  'cap.noCameraBody': 'Pick a photo from the gallery, or draw on the screen instead.',
  'cap.howPermission': 'How to give permission',
  'cap.tryAgain': 'Try again',
  'cap.help1': 'Open your device Settings.',
  'cap.help2': 'Find Sketchling (or your browser) in the list of apps.',
  'cap.help3': 'Turn on Camera, then come back and tap Try again.',
  'cap.helpWeb': 'In a web browser, tap the camera icon next to the address bar and choose Allow.',
  'cap.gotIt': 'Got it',
  'cap.cropTitle': 'Line up the corners',
  'cap.cropBody': 'Drag the dots to the corners of your paper.',
  'cap.cropLabel': 'Photo with crop corners',
  'cap.usePhoto': 'Use photo',
  'cap.processing': 'Cleaning up…',
  'cap.retake': 'Retake',
  'cap.tools': 'Drawing tools',
  'cap.back': 'Back to the camera',
  'cap.pencil': 'Pencil',
  'cap.eraser': 'Eraser',
  'cap.colour': 'Colour {c}',
  'cap.canvasLabel': 'Drawing paper',
  'cap.undo': 'Undo',
  'cap.clear': 'Clear',
  'cap.useDrawing': 'Use this drawing',

  /* --------------------------------- feedback --------------------------------- */
  'fb.status1': 'Teacher Juno is taking a look…',
  'fb.status2': 'Spotting your best lines…',
  'fb.status3': 'Checking the shapes…',
  'fb.status4': 'Almost there…',
  'fb.stars': '{n} of 3 stars',
  'fb.fallbackTitle': 'You finished the lesson!',
  'fb.fallbackBody': 'Every drawing makes your hand a little stronger.',
  'fb.fallbackSpoken': 'Hooray! You finished the lesson. Great job!',
  'fb.reflectTitle': 'Let’s think about {skill}',
  'fb.yes': 'Yes!',
  'fb.kindOf': 'Kind of',
  'fb.notYet': 'Not yet',
  'fb.skill.shapes': 'shapes',
  'fb.skill.patterns': 'patterns',
  'fb.skill.pressure': 'pencil pressure',
  'fb.skill.shading': 'shading',
  'fb.skill.texture': 'texture',
  'fb.skill.fur': 'fur',
  'fb.skill.lineOfAction': 'lines of action',
  'fb.skill.expression': 'expressions',
  'fb.skill.creativity': 'creativity',
  'fb.skill.details': 'details',

  /* ------------------------------- celebrations ------------------------------- */
  'cel.drawingTitle': 'What a drawing!',
  'cel.togetherTitle': 'You drew it together!',
  'cel.plusSparks': '+{n} sparks',
  'cel.forName': 'For {name}',
  'cel.sparksEarned': 'You earned {n} sparks!',
  'cel.levelUp': 'LEVEL {n}',
  'cel.levelUpBody': 'You reached a new level. Keep that pencil moving!',
  'cel.featureUnlocked': 'New surprise unlocked',
  'cel.feature.journal': 'Your journal',
  'cel.feature.tips': 'Tips & tricks',
  'cel.feature.letters': 'Letters from Teacher Juno',
  'cel.feature.stories': 'Sketch Tales',
  'cel.feature.studio': 'My Studio',
  'cel.feature.referenceLibrary': 'Reference library',
  'cel.featureBody.journal': 'A private book that keeps all your drawings.',
  'cel.featureBody.tips': 'Quick tricks to make your drawings shine.',
  'cel.featureBody.letters': 'Send your drawings and get a letter back.',
  'cel.featureBody.stories': 'Short animated stories with drawing ideas.',
  'cel.featureBody.studio': 'Make your own picture, one choice at a time.',
  'cel.featureBody.referenceLibrary': 'Keep a reference picture next to you while you draw.',
  'cel.stickers': '{n} new stickers in your journal',
  'cel.stickersBody': 'Decorate your journal pages with them.',
  'cel.skillUp': 'Skill level {n}!',
  'cel.coinPiece1': 'You got the first piece!',
  'cel.coinPiece2': 'Two pieces down!',
  'cel.coinPiece3': 'The last piece!',
  'cel.coinBody': 'This is the {piece} piece of your coin.',
  'cel.coinComplete': 'Coin complete!',
  'cel.coinCompleteBody': 'Your coin can open something special…',
  'cel.piece.outline': 'outline',
  'cel.piece.pattern': 'pattern',
  'cel.piece.shading': 'shading',
  'cel.keyTitle': 'You earned a key!',
  'cel.keyBody': 'Level {n} of {path} is now open.',
  'cel.pathDone': 'Path complete!',
  'cel.pathDoneBody': 'You finished every lesson in {path}.',
  'cel.challengeCredited': 'It counts for the weekly challenge!',
  'cel.challengeCreditedBody': 'One more slot filled. Keep going!',
  'cel.challengeDone': 'Challenge complete!',
  'cel.challengeDoneBody': 'A treasure chest is waiting for you.',
  'cel.envelopeFull': 'Your envelope is full!',
  'cel.envelopeBody': 'Send your drawings to Teacher Juno and she will write back.',

  /* ------------------------------- draw together ------------------------------- */
  'dt.title': 'Draw together',
  'dt.chip': 'Drawing together',
  'dt.question': 'Is someone drawing with you?',
  'dt.questionBody': 'Pick who is drawing next to you. You will both get sparks!',
  'dt.bannerBody': 'Draw side by side and both earn sparks.',
  'dt.bannerCta': 'Draw together',
  'dt.addSomeone': 'Someone new',
  'dt.name': 'Name',
  'dt.age': 'Age',
  'dt.avatar': 'Pick a face',
  'dt.ageError': 'Age must be between {min} and {max}.',
  'dt.addProfile': 'Add and continue',
  'dt.alone': 'I’m drawing on my own',
  'dt.readyTitle': 'Pencils ready? Both tap Ready!',
  'dt.readyFor': '{name} is ready',
  'dt.change': 'Change partner',
  'dt.start': 'Start drawing',

  /* ----------------------------- share the moment ----------------------------- */
  'stm.whoTitle': 'Who’s nearby to see your art?',
  'stm.whoBody': 'Show them your drawing and let them give you an award.',
  'stm.who.mom': 'Mom',
  'stm.who.dad': 'Dad',
  'stm.who.sibling': 'Brother or sister',
  'stm.who.grandparent': 'Grandparent',
  'stm.who.friend': 'Friend',
  'stm.who.other': 'Someone else',
  'stm.notNow': 'Not now',
  'stm.awardTitle': '{who}, pick an award!',
  'stm.award.heart': 'Big heart',
  'stm.award.rainbow': 'Rainbow',
  'stm.award.star': 'Star',
  'stm.award.trophy': 'Trophy',
  'stm.award.special': 'Extra special',
  'stm.complimentTitle': 'Add a kind word',
  'stm.complimentPlaceholder': 'Write your own compliment…',
  'stm.give': 'Give the award',
  'stm.shownTitle': 'An award from {who}!',
  'stm.sendFar': 'Send to someone far away',
  'stm.shareTitle': 'A drawing by {name}',
  'stm.shareText': 'Look what {name} drew! Leave a kind word.',
  'stm.copied': 'Link copied! Paste it in a message.',
  'stm.shareOffline': 'We can’t make a link right now. Try again when you are online.',
  'stm.publicMissing': 'This drawing link has expired or does not exist.',
  'stm.publicOffline': 'We couldn’t load this drawing. Check your connection.',
  'stm.publicBy': 'by {name}',
  'stm.publicTitle': 'Send {name} some love',
  'stm.publicWho': 'Who are you?',
  'stm.publicName': 'Your name (optional)',
  'stm.publicAward': 'Pick an award',
  'stm.publicSend': 'Send compliment',
  'stm.publicSendError': 'That didn’t send. Please try again.',
  'stm.publicThanks': 'Sent! {name} will see it in the journal.',
  'stm.publicThanksAnon': 'Sent! The young artist will see it in their journal.',
  'stm.publicArtist': 'a young artist',

  /* ------------------------------- review prompt ------------------------------- */
  'rev.title': 'Tell us what you think',
  'rev.ask': 'Would you like to tell others what you like about Sketchling?',
  'rev.yes': 'Yes!',
  'rev.notReally': 'Not really',
  'rev.great': 'Great! What did you like most?',
  'rev.like.lessons': 'The lessons',
  'rev.like.paper': 'Drawing on real paper',
  'rev.like.teacher': 'Teacher Juno',
  'rev.like.journal': 'My journal',
  'rev.like.sparks': 'Sparks and levels',
  'rev.like.characters': 'Making characters',
  'rev.sheetTitle': 'Enjoying Sketchling?',
  'rev.sheetBody': 'Tap a star to rate it.',
  'rev.starN': '{n} stars',
  'rev.notNow': 'Not now',
  'rev.submit': 'Submit',
  'rev.thanks': 'Thank you! That made Scribble wag his tail.',
};

export const vi: Record<keyof typeof en, string> = {
  'les.close': 'Rời bài học',
  'les.stepOf': 'Bước {n} / {total}',
  'les.captionShow': 'Hiện lời hướng dẫn',
  'les.captionHide': 'Ẩn lời hướng dẫn',
  'les.partOf': 'Phần {n} / {total}',
  'les.stepN': 'Bước {n}',
  'les.captions': 'Phụ đề',
  'les.music': 'Nhạc nền',
  'les.voice': 'Giọng cô giáo',
  'les.leaveTitle': 'Rời bài học này nhé?',
  'les.leaveBody': 'Đừng lo, mình sẽ giữ chỗ để con học tiếp sau.',
  'les.leave': 'Rời đi',
  'les.stay': 'Vẽ tiếp',
  'les.notFound': 'Ơ, bài học này đi lạc mất rồi.',
  'les.missing': 'Phần này đang được vẽ thêm. Mình đi tiếp nhé!',
  'les.yourTurn': 'Đến lượt con!',
  'les.yourTurnBody': 'Vẽ bước này lên giấy nhé. Xong thì chạm Sẵn sàng.',
  'les.yourTurnTogether': 'Vẽ bước này lên giấy nhé. Chờ {name} xong rồi hãy chạm Sẵn sàng.',
  'les.yourTurnSpoken': 'Đến lượt con! Vẽ bước này lên giấy, rồi chạm Sẵn sàng nhé.',
  'les.readyDone': 'Sẵn sàng, xong rồi',
  'les.catchUp': 'Xem lại',
  'les.catchUpTogether': 'Cùng xem lại',
  'les.tipsTitle': 'Hướng dẫn & mẹo',
  'les.tips': 'Xem mẹo',
  'les.reference': 'Hiện hình mẫu',
  'les.hideReference': 'Ẩn hình mẫu',
  'les.playerControls': 'Điều khiển bài học',
  'les.back10': 'Lùi 10 giây',
  'les.fwd10': 'Tới 10 giây',
  'les.play': 'Phát',
  'les.pause': 'Tạm dừng',
  'les.seek': 'Dòng thời gian bài học',
  'les.speed': 'Tốc độ {n}×',
  'les.gotIt': 'Hiểu rồi',
  'les.nextTip': 'Mẹo tiếp theo',
  'les.notNow': 'Để sau',
  'les.letsGo': 'Đi thôi',
  'les.redirect.playground': 'Còn rất nhiều tranh vẽ đang chờ con ở sân chơi.',
  'les.redirect.journal': 'Nhật ký giữ lại mọi bức tranh con vẽ.',
  'les.redirect.studio': 'Tự thiết kế bức tranh của riêng con trong xưởng vẽ.',
  'les.redirect.challenges': 'Xem thử thách tuần này và lấp đầy các ô nhé.',
  'les.redirect.path': 'Bài học tiếp theo đã sẵn sàng trên lộ trình.',
  'les.interactNice': 'Giỏi lắm! Thử lần nữa hoặc chạm Hiểu rồi.',
  'les.interactHint.duplicate': 'Chạm vào hình để sao chép nó.',
  'les.interactHint.scatter': 'Chạm để rắc những nét nhỏ.',
  'les.interactHint.gravity': 'Chạm nút và xem nhé!',
  'les.interactHint.shadow': 'Di chuyển mặt trời và xem cái bóng.',
  'les.interactHint.pressure': 'Trượt để ấn nhẹ hoặc mạnh tay hơn.',
  'les.interactHint.symmetry': 'Chạm vào một bên. Bên kia sẽ bắt chước theo!',
  'les.demo.duplicate': 'Sao chép hình',
  'les.demo.scatter': 'Rắc nét vẽ',
  'les.demo.gravity': 'Bật trọng lực',
  'les.demo.gravityUp': 'Bay lên lại',
  'les.demo.shadow': 'Di chuyển mặt trời',
  'les.demo.pressure': 'Lực ấn bút chì',
  'les.demo.light': 'nhẹ',
  'les.demo.medium': 'vừa',
  'les.demo.hard': 'mạnh',
  'les.demo.symmetry': 'Thêm chấm đối xứng',
  'les.interactHint.arrows': 'Chạm một mũi tên để xoay hướng nét.',
  'les.interactHint.bodyDrag': 'Kéo quả bóng và xem cơ thể uốn cong.',
  'les.demo.arrows': 'Xoay hướng nét',
  'les.demo.bodyDrag': 'Uốn cong cơ thể',
  'les.revealSlider': 'Thanh trượt so sánh',
  'les.off': 'Tắt',
  'les.on': 'Bật',
  'les.toggleLabel': 'Hiện bước cuối',
  'les.chooseThis': 'Chọn hình này',
  'les.builderParts': 'Các phần của nhân vật',
  'les.part.face': 'Khuôn mặt',
  'les.part.ears': 'Tai',
  'les.part.eyes': 'Mắt',
  'les.part.brows': 'Lông mày',
  'les.part.mouth': 'Miệng',
  'les.part.eyeDistance': 'Khoảng cách mắt',
  'les.part.eyeHeight': 'Độ cao mắt',
  'les.part.mouthHeight': 'Độ cao miệng',
  'les.part.color': 'Màu sắc',
  'les.slider.low': 'Thấp',
  'les.slider.mid': 'Giữa',
  'les.slider.high': 'Cao',
  'les.startDrawing': 'Bắt đầu vẽ',
  'les.turnOf': 'Lượt của {name}',
  'les.turnBody': 'Đưa máy cho bạn nhé: đến lúc chụp bức tranh tiếp theo rồi.',
  'les.snapTurn': 'Chụp tranh của {name}',
  'les.yourDrawing': 'Tranh của con',
  'les.photoLooksGood': 'Đẹp quá! Dùng ảnh này nhé?',
  'les.usePhoto': 'Dùng ảnh này',
  'les.retake': 'Chụp lại',
  'les.skipPhoto': 'Bỏ qua',
  'les.myDrawing': 'Tranh của tớ',
  'les.journalTitle': 'Thêm vào nhật ký',
  'les.titleLabel': 'Tên tranh',
  'les.descLabel': 'Mô tả',
  'les.descPlaceholder': 'Viết vài dòng về bức tranh (không bắt buộc)',
  'les.feelingQuestion': 'Con thấy bức tranh của mình thế nào?',
  'les.feelingLabel': 'Con thấy thế nào?',
  'les.feeling.1': 'Không vui',
  'les.feeling.2': 'Hơi phân vân',
  'les.feeling.3': 'Bình thường',
  'les.feeling.4': 'Vui',
  'les.feeling.5': 'Siêu tự hào',
  'les.filterLabel': 'Bộ lọc ảnh',
  'les.filter.none': 'Gốc',
  'les.filter.clean': 'Sắc nét',
  'les.filter.warm': 'Nắng ấm',
  'les.filter.cool': 'Mát mẻ',
  'les.filter.mono': 'Bút chì',
  'les.filter.pop': 'Rực rỡ',
  'les.addToJournal': 'Thêm vào nhật ký',
  'les.reason.stepByStep': 'Học từng bước một',
  'les.reason.fun': 'Rất vui',
  'les.reason.proud': 'Con tự hào về tranh của mình',
  'les.reason.tooHard': 'Hơi khó một chút',
  'les.reason.tooEasy': 'Dễ quá',
  'les.reason.wantHelp': 'Con muốn được giúp thêm',
  'les.send': 'Gửi',
  'les.pathDoneTitle': 'Con đã hoàn thành cả lộ trình!',
  'les.pathDoneBody': 'Con làm tuyệt lắm với {path}. Tiếp theo mình vẽ gì nhỉ?',
  'les.later': 'Để sau',

  'cap.frameTitle': 'Đặt bức tranh vào trong khung',
  'cap.calibrationTitle': 'Vẽ một hình tròn lên giấy, rộng bằng cao',
  'cap.journalTitle': 'Chụp một bức tranh cho nhật ký',
  'cap.canvasTitle': 'Vẽ ngay tại đây bằng ngón tay',
  'cap.close': 'Đóng máy ảnh',
  'cap.reference': 'Hình mẫu',
  'cap.refTitle': 'Vẽ hình này nhé',
  'cap.refHide': 'Ẩn hình mẫu',
  'cap.refShow': 'Xem hình mẫu',
  'cap.refEnlarge': 'Phóng to hình mẫu',
  'cap.skip': 'Để sau nhé',
  'cap.shutter': 'Chụp ảnh',
  'cap.gallery': 'Chọn từ thư viện',
  'cap.drawOnScreen': 'Vẽ trên màn hình',
  'cap.starting': 'Đang mở máy ảnh…',
  'cap.deniedTitle': 'Cần máy ảnh để xem tranh của con',
  'cap.deniedBody': 'Nhờ bố mẹ cho phép dùng máy ảnh, hoặc chọn ảnh từ thư viện nhé.',
  'cap.noCameraTitle': 'Không tìm thấy máy ảnh',
  'cap.noCameraBody': 'Chọn một ảnh từ thư viện, hoặc vẽ ngay trên màn hình.',
  'cap.howPermission': 'Cách cấp quyền',
  'cap.tryAgain': 'Thử lại',
  'cap.help1': 'Mở Cài đặt của thiết bị.',
  'cap.help2': 'Tìm Sketchling (hoặc trình duyệt) trong danh sách ứng dụng.',
  'cap.help3': 'Bật Máy ảnh, rồi quay lại và chạm Thử lại.',
  'cap.helpWeb': 'Trên trình duyệt, chạm biểu tượng máy ảnh cạnh thanh địa chỉ và chọn Cho phép.',
  'cap.gotIt': 'Hiểu rồi',
  'cap.cropTitle': 'Căn bốn góc',
  'cap.cropBody': 'Kéo các chấm tới bốn góc tờ giấy.',
  'cap.cropLabel': 'Ảnh với các góc cắt',
  'cap.usePhoto': 'Dùng ảnh',
  'cap.processing': 'Đang làm đẹp…',
  'cap.retake': 'Chụp lại',
  'cap.tools': 'Dụng cụ vẽ',
  'cap.back': 'Quay lại máy ảnh',
  'cap.pencil': 'Bút chì',
  'cap.eraser': 'Tẩy',
  'cap.colour': 'Màu {c}',
  'cap.canvasLabel': 'Giấy vẽ',
  'cap.undo': 'Hoàn tác',
  'cap.clear': 'Xoá hết',
  'cap.useDrawing': 'Dùng bức vẽ này',

  'fb.status1': 'Cô Juno đang ngắm tranh…',
  'fb.status2': 'Đang tìm những nét đẹp nhất…',
  'fb.status3': 'Đang xem các hình khối…',
  'fb.status4': 'Sắp xong rồi…',
  'fb.stars': '{n} trên 3 sao',
  'fb.fallbackTitle': 'Con đã hoàn thành bài học!',
  'fb.fallbackBody': 'Mỗi bức tranh giúp bàn tay con khéo hơn một chút.',
  'fb.fallbackSpoken': 'Hoan hô! Con đã hoàn thành bài học. Giỏi lắm!',
  'fb.reflectTitle': 'Cùng nghĩ về {skill} nhé',
  'fb.yes': 'Có!',
  'fb.kindOf': 'Hơi hơi',
  'fb.notYet': 'Chưa ạ',
  'fb.skill.shapes': 'hình khối',
  'fb.skill.patterns': 'hoa văn',
  'fb.skill.pressure': 'lực ấn bút',
  'fb.skill.shading': 'tô bóng',
  'fb.skill.texture': 'chất liệu',
  'fb.skill.fur': 'lông thú',
  'fb.skill.lineOfAction': 'đường chuyển động',
  'fb.skill.expression': 'biểu cảm',
  'fb.skill.creativity': 'sáng tạo',
  'fb.skill.details': 'chi tiết',

  'cel.drawingTitle': 'Bức tranh tuyệt quá!',
  'cel.togetherTitle': 'Hai bạn đã cùng vẽ xong!',
  'cel.plusSparks': '+{n} tia sáng',
  'cel.forName': 'Dành cho {name}',
  'cel.sparksEarned': 'Con nhận được {n} tia sáng!',
  'cel.levelUp': 'CẤP {n}',
  'cel.levelUpBody': 'Con đã lên cấp mới. Cứ tiếp tục vẽ nhé!',
  'cel.featureUnlocked': 'Mở khoá bất ngờ mới',
  'cel.feature.journal': 'Nhật ký của con',
  'cel.feature.tips': 'Mẹo hay',
  'cel.feature.letters': 'Thư từ cô Juno',
  'cel.feature.stories': 'Truyện Sketch Tales',
  'cel.feature.studio': 'Xưởng vẽ của tớ',
  'cel.feature.referenceLibrary': 'Thư viện hình mẫu',
  'cel.featureBody.journal': 'Cuốn sổ riêng giữ lại mọi bức tranh của con.',
  'cel.featureBody.tips': 'Những mẹo nhanh giúp tranh của con thêm đẹp.',
  'cel.featureBody.letters': 'Gửi tranh cho cô và nhận thư hồi âm.',
  'cel.featureBody.stories': 'Những câu chuyện hoạt hình ngắn đầy ý tưởng vẽ.',
  'cel.featureBody.studio': 'Tự tạo bức tranh của riêng con, từng lựa chọn một.',
  'cel.featureBody.referenceLibrary': 'Để hình mẫu ngay bên cạnh khi con vẽ.',
  'cel.stickers': '{n} hình dán mới trong nhật ký',
  'cel.stickersBody': 'Dùng chúng để trang trí các trang nhật ký nhé.',
  'cel.skillUp': 'Kỹ năng cấp {n}!',
  'cel.coinPiece1': 'Con có mảnh đầu tiên rồi!',
  'cel.coinPiece2': 'Đã có hai mảnh!',
  'cel.coinPiece3': 'Mảnh cuối cùng!',
  'cel.coinBody': 'Đây là mảnh {piece} của đồng xu.',
  'cel.coinComplete': 'Đồng xu đã hoàn chỉnh!',
  'cel.coinCompleteBody': 'Đồng xu này mở được một điều đặc biệt…',
  'cel.piece.outline': 'đường viền',
  'cel.piece.pattern': 'hoa văn',
  'cel.piece.shading': 'tô bóng',
  'cel.keyTitle': 'Con nhận được một chiếc chìa khoá!',
  'cel.keyBody': 'Cấp {n} của {path} đã mở.',
  'cel.pathDone': 'Hoàn thành lộ trình!',
  'cel.pathDoneBody': 'Con đã học hết mọi bài trong {path}.',
  'cel.challengeCredited': 'Bức này được tính cho thử thách tuần!',
  'cel.challengeCreditedBody': 'Thêm một ô nữa đã đầy. Cố lên!',
  'cel.challengeDone': 'Hoàn thành thử thách!',
  'cel.challengeDoneBody': 'Một rương báu đang chờ con.',
  'cel.envelopeFull': 'Phong bì của con đã đầy!',
  'cel.envelopeBody': 'Gửi tranh cho cô Juno và cô sẽ viết thư trả lời.',

  'dt.title': 'Cùng vẽ',
  'dt.chip': 'Đang cùng vẽ',
  'dt.question': 'Có ai đang vẽ cùng con không?',
  'dt.questionBody': 'Chọn người đang vẽ bên cạnh con. Cả hai đều nhận tia sáng!',
  'dt.bannerBody': 'Vẽ cạnh nhau và cả hai cùng nhận tia sáng.',
  'dt.bannerCta': 'Cùng vẽ',
  'dt.addSomeone': 'Thêm người mới',
  'dt.name': 'Tên',
  'dt.age': 'Tuổi',
  'dt.avatar': 'Chọn khuôn mặt',
  'dt.ageError': 'Tuổi phải từ {min} đến {max}.',
  'dt.addProfile': 'Thêm và tiếp tục',
  'dt.alone': 'Con vẽ một mình',
  'dt.readyTitle': 'Bút chì sẵn sàng chưa? Cả hai chạm Sẵn sàng nhé!',
  'dt.readyFor': '{name} sẵn sàng',
  'dt.change': 'Đổi bạn vẽ',
  'dt.start': 'Bắt đầu vẽ',

  'stm.whoTitle': 'Ai đang ở gần để xem tranh của con?',
  'stm.whoBody': 'Khoe bức tranh và để họ trao cho con một giải thưởng.',
  'stm.who.mom': 'Mẹ',
  'stm.who.dad': 'Bố',
  'stm.who.sibling': 'Anh chị em',
  'stm.who.grandparent': 'Ông bà',
  'stm.who.friend': 'Bạn bè',
  'stm.who.other': 'Người khác',
  'stm.notNow': 'Để sau',
  'stm.awardTitle': '{who} ơi, chọn một giải thưởng nhé!',
  'stm.award.heart': 'Trái tim to',
  'stm.award.rainbow': 'Cầu vồng',
  'stm.award.star': 'Ngôi sao',
  'stm.award.trophy': 'Cúp vàng',
  'stm.award.special': 'Siêu đặc biệt',
  'stm.complimentTitle': 'Thêm một lời khen',
  'stm.complimentPlaceholder': 'Tự viết lời khen…',
  'stm.give': 'Trao giải',
  'stm.shownTitle': 'Giải thưởng từ {who}!',
  'stm.sendFar': 'Gửi cho người ở xa',
  'stm.shareTitle': 'Một bức tranh của {name}',
  'stm.shareText': 'Xem {name} vẽ này! Gửi một lời khen nhé.',
  'stm.copied': 'Đã chép đường dẫn! Dán vào tin nhắn nhé.',
  'stm.shareOffline': 'Chưa tạo được đường dẫn lúc này. Thử lại khi có mạng nhé.',
  'stm.publicMissing': 'Đường dẫn bức tranh này đã hết hạn hoặc không tồn tại.',
  'stm.publicOffline': 'Không tải được bức tranh. Kiểm tra kết nối mạng nhé.',
  'stm.publicBy': 'của {name}',
  'stm.publicTitle': 'Gửi yêu thương cho {name}',
  'stm.publicWho': 'Bạn là ai?',
  'stm.publicName': 'Tên của bạn (không bắt buộc)',
  'stm.publicAward': 'Chọn một giải thưởng',
  'stm.publicSend': 'Gửi lời khen',
  'stm.publicSendError': 'Chưa gửi được. Vui lòng thử lại.',
  'stm.publicThanks': 'Đã gửi! {name} sẽ thấy trong nhật ký.',
  'stm.publicThanksAnon': 'Đã gửi! Hoạ sĩ nhí sẽ thấy trong nhật ký.',
  'stm.publicArtist': 'hoạ sĩ nhí',

  'rev.title': 'Cho chúng mình biết ý kiến nhé',
  'rev.ask': 'Con có muốn kể cho mọi người nghe con thích gì ở Sketchling không?',
  'rev.yes': 'Có!',
  'rev.notReally': 'Không hẳn',
  'rev.great': 'Tuyệt! Con thích điều gì nhất?',
  'rev.like.lessons': 'Các bài học',
  'rev.like.paper': 'Vẽ trên giấy thật',
  'rev.like.teacher': 'Cô Juno',
  'rev.like.journal': 'Nhật ký của con',
  'rev.like.sparks': 'Tia sáng và cấp độ',
  'rev.like.characters': 'Tạo nhân vật',
  'rev.sheetTitle': 'Con thích Sketchling chứ?',
  'rev.sheetBody': 'Chạm vào ngôi sao để đánh giá.',
  'rev.starN': '{n} sao',
  'rev.notNow': 'Để sau',
  'rev.submit': 'Gửi',
  'rev.thanks': 'Cảm ơn con! Scribble vui đến vẫy cả đuôi.',
};
